import { useState } from "react";
import { planIncludesModule } from "../../../../constants/planMarketing";
import { PageSeo } from "../seo";
import { TRIAL_DAYS } from "../../../../constants/trial";
import site from "../site.module.css";
import h from "./home.module.css";
import {
  FaqSection,
  formatRub,
  PageHero,
  RegisterSection,
  Title,
  usePublicPlans,
} from "./parts";

const PLAN_STORY = {
  start: {
    who: "Для тренера, который ведёт группы сам",
    lead: "Расписание, журнал, абонементы и учёт оплат. Владелец — он же тренер.",
    example: "Тренер по карате ведёт две детские группы — 25 учеников, один зал.",
    included: [
      "Расписание, группы и направления",
      "Журнал посещений с автосписанием",
      "Абонементы и учёт оплат",
      "Чат с клиентами и push",
    ],
    later: "Кабинет клиента и кабинет тренера можно подключить модулем.",
    photo: "/landing/niche/futbol/split.jpg",
    phone: "mock-owner-journal.png",
    float: ["Тренер — владелец", "Журнал и абонементы"],
  },
  club: {
    who: "Для секции с командой тренеров",
    lead: "У каждого тренера свой кабинет, у клиентов — приложение клуба с расписанием, абонементом и онлайн-оплатой.",
    example: "Школа танцев: 4 педагога, 180 учеников, 2 зала.",
    included: [
      "Расписание, журнал, абонементы и чат",
      "Кабинет тренера: группы, расписание, журнал",
      "Приложение клуба для клиентов и родителей",
      "Онлайн-оплата через ЮKassa или Т-Банк",
      "Опросы и логотип клуба",
    ],
    later: "Полный брендинг и соцсеть участников — модулями.",
    photo: "/landing/niche/tancy/hero.jpg",
    photoPos: "0% 50%",
    phone: "mock-client-home.png",
    float: ["Кабинеты тренера и клиента", "Онлайн-оплата"],
  },
  scale: {
    who: "Для крупной школы или сети залов",
    lead: "Приложение полностью под вашим брендом, лента и соцсеть клуба, приоритетная поддержка и запас по лимитам.",
    example: "Школа плавания: 15 тренеров, 900 учеников, 5 бассейнов.",
    included: [
      "Кабинеты тренера и клиента, онлайн-оплата",
      "Цвета, обложка и свой экран загрузки",
      "Клиенты не видят Бай.Спорт",
      "Лента клуба и соцсеть участников",
      "Приоритетная поддержка",
    ],
    later: null,
    photo: "/landing/niche/plavanie/hero.jpg",
    photoPos: "0% 50%",
    phone: "mock-brand-splash.png",
    float: ["Приложение под вашим брендом", "Лента и соцсеть клуба"],
  },
};

const ROWS = [
  ["Основа", null],
  ["Расписание, группы и направления", () => true],
  ["Журнал посещений", () => true],
  ["Абонементы и учёт оплат", (f) => f.finance],
  ["Чат с клиентами и push", (f) => f.chat && f.push],
  ["Онлайн-оплата (ЮKassa, Т-Банк)", (f) => f.online_acquiring],
  ["Кабинеты", null],
  ["Кабинет тренера", (f) => f.trainer_portal],
  ["Кабинет клиента", (f) => f.client_portal],
  ["Опросы", (f) => f.polls],
  ["Лента и соцсеть участников", (f) => f.member_social],
  ["Бренд", null],
  ["Логотип клуба", (f) => f.branding_lite || f.branding],
  ["Цвета, обложка и свой экран загрузки", (f) => f.branding],
  ["Клиенты не видят Бай.Спорт", (f) => f.white_label],
  ["Поддержка", null],
  ["Приоритетная поддержка", (f) => f.priority_support],
];

const FAQ = [
  [
    "Нужно ли платить сразу?",
    `Нет. Первые ${TRIAL_DAYS} дней — полный доступ бесплатно, карту привязывать не нужно. Тариф выбираете, когда решите продолжить.`,
  ],
  [
    "Цена — за клуб или за каждого тренера?",
    "За клуб. Тренеры, клиенты, направления и залы — в пределах лимитов тарифа, без доплаты за каждого пользователя.",
  ],
  [
    "Что, если клуб вырастет из тарифа?",
    "Перейдите на следующий тариф в кабинете клуба. Клиенты, абонементы и история посещений остаются на месте.",
  ],
  [
    "Как работают модули?",
    "Модуль добавляет возможность, которой нет в вашем тарифе, — например, кабинет клиента в «Старте». Подключается в кабинете клуба, стоимость прибавляется к тарифу.",
  ],
  [
    "Есть ли комиссия Бай.Спорт с онлайн-оплат?",
    "Оплата идёт через ваш договор с ЮKassa или Т-Банком, деньги приходят напрямую на счёт клуба. Условия эквайринга — по тарифам банка.",
  ],
];

function limitsOf(f) {
  return [
    [f.max_trainers === 1 ? "1" : formatRub(f.max_trainers), f.max_trainers === 1 ? "тренер" : "тренеров"],
    [formatRub(f.max_clients), "клиентов"],
    [formatRub(f.max_directions), "направлений"],
    [formatRub(f.max_branches), f.max_branches === 1 ? "зал" : "залов"],
  ];
}

function PlanChapter({ plan, index, total, featured }) {
  const story = PLAN_STORY[plan.code];
  if (!story) return null;
  const f = plan.features || {};
  const flip = index % 2 === 1;

  return (
    <section
      id={`plan-${plan.code}`}
      className={`${h.section} ${h.featSection} ${flip ? h.soft : ""}`}
    >
      <div className={`${h.wrap} ${h.split} ${flip ? h.splitFlip : ""}`}>
        <div className={h.collage} aria-hidden>
          <div className={h.collagePhoto}>
            <img
              src={story.photo}
              alt=""
              loading="lazy"
              style={story.photoPos ? { objectPosition: story.photoPos } : undefined}
            />
          </div>
          <div className={`${h.float} ${h.floatClass}`}>
            <small>Тариф «{plan.name}»</small>
            <b>{story.float[0]}</b>
            <div className={h.meterRow}>
              {story.float[1]} <span>✓</span>
            </div>
          </div>
          <img src={`/landing/${story.phone}`} alt="" className={h.heroPhone} />
        </div>

        <div>
          <p className={h.kicker}>
            Тариф {index + 1} из {total}
            {featured ? " · Популярный" : ""}
          </p>
          <div className={h.planHead}>
            <h2 className={h.h2}>{plan.name}</h2>
            <p className={h.planHeadPrice}>
              {formatRub(plan.monthly_price_rub)} ₽<small> / мес за клуб</small>
            </p>
          </div>
          <p className={h.planWho}>{story.who}</p>
          <p className={h.sub}>{story.lead}</p>

          <div className={h.planExample}>
            <small>Например</small>
            {story.example}
          </div>

          <ul className={h.featList}>
            {story.included.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>

          <div className={h.limits}>
            {limitsOf(f).map(([n, label]) => (
              <div key={label}>
                <b>{n}</b>
                <span>{label}</span>
              </div>
            ))}
          </div>

          {story.later ? <p className={h.planLater}>{story.later}</p> : null}

          <div className={h.planCta}>
            <a href="#register" className={`${site.btn} ${site.btnLg}`}>
              Начать бесплатно
            </a>
            <span>
              {TRIAL_DAYS} дней бесплатно, затем {formatRub(plan.monthly_price_rub)} ₽ / мес
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function ModulesCalculator({ plans, modules }) {
  const [planCode, setPlanCode] = useState("club");
  const [picked, setPicked] = useState(() => new Set());
  const plan = plans.find((p) => p.code === planCode) || plans[0];
  const base = Number(plan?.monthly_price_rub) || 0;

  const rows = modules.map((m) => ({
    ...m,
    included: planIncludesModule(plan, m.code),
  }));
  const addons = rows.filter((m) => !m.included && picked.has(m.code));
  const total = base + addons.reduce((s, m) => s + (Number(m.price_rub) || 0), 0);

  const toggle = (code) =>
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  return (
    <section className={h.section} id="calc">
      <div className={h.wrap}>
        <div className={h.headRow}>
          <div>
            <p className={h.kicker}>Модули</p>
            <Title lines={["Нужно что-то ещё —", "добавьте модуль."]} />
            <p className={h.sub}>
              Выберите тариф и отметьте модули — итог пересчитается сразу.
            </p>
          </div>
          <div className={h.tabs} role="tablist" aria-label="Тариф для расчёта">
            {plans.map((p) => (
              <button
                key={p.code}
                type="button"
                role="tab"
                aria-selected={p.code === plan?.code}
                className={`${h.tab} ${p.code === plan?.code ? h.tabOn : ""}`}
                onClick={() => setPlanCode(p.code)}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>

        <div className={h.modLayout}>
          <div className={h.modCards}>
            {rows.map((m) => {
              const on = m.included || picked.has(m.code);
              return (
                <button
                  key={m.code}
                  type="button"
                  className={`${h.modCard} ${on ? h.modCardOn : ""} ${
                    m.included ? h.modCardIncluded : ""
                  }`}
                  onClick={() => !m.included && toggle(m.code)}
                  aria-pressed={on}
                  disabled={m.included}
                >
                  <span className={h.modCardBody}>
                    <span className={h.modTop}>
                      <b>{m.name}</b>
                      <em>{m.included ? "В тарифе" : on ? "✓" : "+"}</em>
                    </span>
                    <span className={h.modDesc}>{m.description}</span>
                    <span className={h.modPrice}>
                      {m.included
                        ? `Входит в «${plan.name}»`
                        : `+${formatRub(m.price_rub)} ₽ / мес`}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <aside className={h.calcSum}>
            <p className={h.calcLabel}>Итого в месяц</p>
            <p className={h.calcTotal}>{formatRub(total)} ₽</p>
            <ul className={h.calcLines}>
              <li>
                <span>Тариф «{plan?.name}»</span>
                <b>{formatRub(base)} ₽</b>
              </li>
              {addons.map((m) => (
                <li key={m.code}>
                  <span>{m.name}</span>
                  <b>+{formatRub(m.price_rub)} ₽</b>
                </li>
              ))}
            </ul>
            <a href="#register" className={`${site.btn} ${h.calcBtn}`}>
              Создать клуб
            </a>
            <p className={h.calcNote}>
              Сейчас платить не нужно — первые {TRIAL_DAYS} дней бесплатно.
            </p>
          </aside>
        </div>
      </div>
    </section>
  );
}

export default function PricingPage() {
  const { plans, modules } = usePublicPlans();
  const featuredCode = plans.find((p) => p.badge)?.code;

  return (
    <>
      <PageSeo page="pricing" faq={FAQ} />

      <PageHero
        pill="Тарифы CRM для спортивного клуба"
        lines={["Понятная цена", "за весь клуб."]}
        lead={`Три тарифа под размер клуба и модули, если нужно что-то ещё. Без доплат за каждого тренера. Первые ${TRIAL_DAYS} дней — бесплатно, без привязки карты.`}
        actions={
          <div className={h.priceJump}>
            {plans.map((p) => (
              <a
                key={p.code}
                href={`#plan-${p.code}`}
                className={p.code === featuredCode ? h.priceJumpOn : ""}
              >
                <small>{p.name}</small>
                <b>{formatRub(p.monthly_price_rub)} ₽</b>
                <em>в месяц</em>
              </a>
            ))}
          </div>
        }
        note={`${TRIAL_DAYS} дней полного доступа • без привязки карты • смена тарифа в любой момент`}
        visual={
          <>
            <div className={h.collagePhoto}>
              <img src="/landing/niche/gimnastika/hero.jpg" alt="" />
            </div>
            <div className={`${h.float} ${h.floatStat}`}>
              <small>Цена за клуб</small>
              <b>
                от {formatRub(plans[0]?.monthly_price_rub)} ₽
              </b>
              <small>без доплат за тренеров</small>
            </div>
            <img src="/landing/mock-brand-manage.png" alt="" className={h.heroPhone} />
          </>
        }
      />

      {plans.map((plan, i) => (
        <PlanChapter
          key={plan.code}
          plan={plan}
          index={i}
          total={plans.length}
          featured={plan.code === featuredCode}
        />
      ))}

      <ModulesCalculator plans={plans} modules={modules} />

      <section className={`${h.section} ${h.soft}`}>
        <div className={h.wrap}>
          <details className={h.compareDetails}>
            <summary>
              <span>
                <b>Подробное сравнение тарифов</b>
                <small>Все возможности «Старта», «Клуба» и «Про» в одной таблице</small>
              </span>
              <em aria-hidden>+</em>
            </summary>
            <div className={h.compareWrap}>
              <table className={h.compare}>
                <thead>
                  <tr>
                    <th />
                    {plans.map((p) => (
                      <th key={p.code}>
                        <b>{p.name}</b>
                        <small>{formatRub(p.monthly_price_rub)} ₽ / мес</small>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map(([label, get]) =>
                    get ? (
                      <tr key={label}>
                        <td>{label}</td>
                        {plans.map((p) => (
                          <td key={p.code}>
                            {get(p.features || {}) ? (
                              <span className={h.yes}>✓</span>
                            ) : (
                              <span className={h.no}>—</span>
                            )}
                          </td>
                        ))}
                      </tr>
                    ) : (
                      <tr key={label} className={h.compareGroup}>
                        <td colSpan={plans.length + 1}>{label}</td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          </details>
        </div>
      </section>

      <FaqSection kicker="Вопросы об оплате" items={FAQ} />

      <RegisterSection
        kicker="Регистрация"
        title={["Начните бесплатно —", "тариф выберете потом"]}
        soft
      />
    </>
  );
}
